'use client';

import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';

interface LoaderContextType {
  isLoading: boolean;
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

export const LoaderProvider = ({ children }: { children: ReactNode }) => {
  // Never show any loader on the password-protection page.
  const isPasswordPage = typeof window !== "undefined" && window.location.pathname === "/password";
  const [isLoading, setIsLoading] = useState(!isPasswordPage);

  useEffect(() => {
    if (isPasswordPage) return; // No loader events on /password

    const handleLoaderEvent = (event: CustomEvent) => {
      if (event.detail === 'show') {
        setIsLoading(true);
      } else if (event.detail === 'hide') {
        setIsLoading(false);
      } else if (event.detail === 'splash-done') {
        setIsLoading(false);
      }
    };

    window.addEventListener('loader', handleLoaderEvent as EventListener);

    // Dismiss the initial splash once the shell has mounted/painted. The splash
    // is only an anti-FOUC cover, so dismissal must not depend on catching a
    // transient 'splash-done' event — that event is dispatched from a layout
    // effect, which runs before this passive listener attaches, so it is missed
    // and the splash would otherwise stay stuck. Self-dismissing here is
    // deterministic and race-free. The 'show'/'hide' events above still drive
    // the API loader after this point.
    setIsLoading(false);

    return () => {
      window.removeEventListener('loader', handleLoaderEvent as EventListener);
    };
  }, [isPasswordPage]);

  return (
    <LoaderContext.Provider value={{ isLoading }}>
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (context === undefined) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
}; 